// oxlint-disable max-statements, max-lines-per-function
import { isZeroDiscountReferral } from '@kp/tiia-pipes';

import type {
  InsuredRoles,
  TempParty,
  TiiaCalculation,
  TiiaInsuredRole,
  TiiaModel,
  TiiaPolicyRoles,
  TiiaSaveAgreement,
  TiInsurableInterest,
  TravelDestination,
} from '@kp/interfaces';
import type { DeepPartial } from '@kp/types';

function calculationToModel(response: TiiaCalculation): DeepPartial<TiiaModel> {
  const mainRole = response.roles.find(
    role => role.party.partyIdentifiers[0]?.partyIdentifierValue === '1',
  ) as InsuredRoles | undefined;
  const mainInterest = mainRole?.party.insurableInterests.find(
    interest => interest.insurableInterestTypeCode === 'MAIN',
  ) as TiInsurableInterest | undefined;
  const variant = mainInterest?.variant;

  return {
    temp: {
      recommended: variant,
      variant,
    },
  };
}

function mapTempParty(
  temp: TiiaModel['temp'],
): (party: TempParty, index: number) => TiiaInsuredRole {
  return (party: TempParty, index: number): TiiaInsuredRole => ({
    party: {
      birthDate: party.birthDate,
      insurableInterests: [
        {
          // It should be ok as sync is active after step 0
          beginDate: temp.beginDate || '',
          insurableInterestTypeCode: 'MAIN' as const,
          variant: temp.variant,
        },
        ...(temp.party.onlyPolicy && index === 0
          ? [
              {
                // It should be ok as sync is active after step 0
                beginDate: temp.beginDate || '',
                insurableInterestTypeCode: 'MAIN' as const,
                variant: temp.variant,
              },
              ...(temp.carInterest
                ? [
                    {
                      insurableInterestTypeCode: 'CAR' as const,
                    },
                  ]
                : []),
              ...(temp.stornoAmount && temp.stornoVariant
                ? [
                    {
                      insurableInterestTypeCode: 'CANCEL' as const,
                      sumInsured: temp.stornoAmount,
                      variant: temp.stornoVariant,
                    },
                  ]
                : []),
            ]
          : []),
        ...(temp.flightInterest
          ? [
              {
                insurableInterestTypeCode: 'FLIGHT' as const,
              },
            ]
          : []),
        ...(party.sport?.length
          ? party.sport.map(({ beginDate, endDate }) => ({
              beginDate,
              endDate,
              insurableInterestTypeCode: 'SPORT' as const,
            }))
          : []),
        ...(party.work?.length
          ? party.work.map(({ beginDate, endDate }) => ({
              beginDate,
              endDate,
              insurableInterestTypeCode: 'WORK' as const,
            }))
          : []),
      ],
      name: party.name,
      partyIdentifiers: [
        {
          partyIdentifierTypeCode: 'FEID' as const,
          partyIdentifierValue: (temp.party.onlyPolicy
            ? index + 1
            : index + 2
          ).toString(),
        },
      ],
      partyTypeCode: '1' as const,
      surname: party.surname,
    },
    roleCode: 'INSUREDPERSON' as const,
  });
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- this legacy mapping preserves backend contract shape in one place.
function modelToCalculation(model: TiiaModel): TiiaCalculation {
  const {
    temp: {
      beginDate,
      carInterest,
      discountRate,
      flightInterest,
      party: { birthDate, name, onlyPolicy, sport, surname, work },
      stornoAmount,
      stornoVariant,
      variant,
    },
    ...agreement
  } = model;

  if (!beginDate || !agreement.endDate) {
    throw new Error('invalid data for calculation');
  }

  agreement.additionalAgreementInfo.destination =
    agreement.additionalAgreementInfo.destination.replace(
      '!',
      '',
    ) as TravelDestination;

  const parties = Object.values(model.temp.parties || []);
  const mapParty = mapTempParty(model.temp);

  if (parties.length === 0 && onlyPolicy) {
    parties.push({ birthDate: '', name: '', surname: '' });
  }

  const calculation: TiiaCalculation = {
    ...agreement,
    additionalAgreementInfo: {
      ...agreement.additionalAgreementInfo,
      travelType: 'T',
      ...(discountRate !== undefined ||
      isZeroDiscountReferral(agreement.additionalAgreementInfo)
        ? { discountRate: discountRate || 0 }
        : {}),
    },
    // It should be ok as sync is active after step 0
    endDate: agreement.endDate || '',
    roles: [
      onlyPolicy
        ? {
            party: {
              birthDate,
              name,
              partyIdentifiers: [
                {
                  partyIdentifierTypeCode: 'FEID',
                  partyIdentifierValue: '0',
                },
              ],
              partyTypeCode: '1',
              surname,
            },
            roleCode: 'POLICYHOLDER',
          }
        : {
            party: {
              birthDate,
              insurableInterests: [
                {
                  beginDate,
                  insurableInterestTypeCode: 'MAIN',
                  variant,
                },
                ...(carInterest
                  ? [{ insurableInterestTypeCode: 'CAR' as const }]
                  : []),
                ...(flightInterest
                  ? [
                      {
                        insurableInterestTypeCode: 'FLIGHT' as const,
                      },
                    ]
                  : []),
                ...(stornoAmount && stornoVariant
                  ? [
                      {
                        insurableInterestTypeCode: 'CANCEL' as const,
                        sumInsured: stornoAmount,
                        variant: stornoVariant,
                      },
                    ]
                  : []),
                ...(sport?.length
                  ? sport.map(({ beginDate: sportBeginDate, endDate }) => ({
                      beginDate: sportBeginDate,
                      endDate,
                      insurableInterestTypeCode: 'SPORT' as const,
                    }))
                  : []),
                ...(work?.length
                  ? work.map(({ beginDate: workBeginDate, endDate }) => ({
                      beginDate: workBeginDate,
                      endDate,
                      insurableInterestTypeCode: 'WORK' as const,
                    }))
                  : []),
              ],
              name,
              partyIdentifiers: [
                {
                  partyIdentifierTypeCode: 'FEID',
                  partyIdentifierValue: '1',
                },
              ],
              partyTypeCode: '1',
              surname,
            },
            roleCode: 'INSUREDANDPOLICYHOLDER',
          },
      ...parties.map((party, index) => mapParty(party, index)),
    ],
  };

  return trimStringsInObj(calculation);
}

const conclusionMethodCode = {
  KB_BAPO: 'IN_BRANCH',
  KB_TC: 'REMOTE_WITH',
} as const;
function modelToAgreement(
  calculation: TiiaCalculation,
  model: TiiaModel,
): TiiaSaveAgreement {
  const referralCode = calculation.additionalAgreementInfo.referralCode
    ?.split('_')[0]
    ?.toLocaleUpperCase() as 'ALD' | 'CKBT' | 'KB' | 'KP';

  const networkCode =
    referralCode === 'CKBT'
      ? 'BROKER'
      : ((referralCode ?? 'KP') as 'ALD' | 'BROKER' | 'KB' | 'KP');

  const agreement: TiiaSaveAgreement = {
    ...calculation,
    agreementStatusCode: 'UC',
    conclusionMethodCode:
      // We know that referralCode is not definitive key of conclusion methods, but if it is undefined it will continue
      conclusionMethodCode[
        calculation.additionalAgreementInfo
          .referralCode as keyof typeof conclusionMethodCode
      ] || 'REMOTE_WITHOUT',
    networkCode,
    productCode: '50202',
  };
  const tempParty = model.temp.party;

  const index = agreement.roles.findIndex(
    role =>
      role.roleCode === 'INSUREDANDPOLICYHOLDER' ||
      role.roleCode === 'POLICYHOLDER',
  );

  const { party } = agreement.roles[index] as TiiaPolicyRoles;
  // eslint-disable-next-line camelcase
  party.additionalPartyInfo = { phone_country_code: '+420' };
  party.contacts = [
    ...(tempParty.phone
      ? ([{ contactTypeCode: '100', contactValue: tempParty.phone }] as const)
      : []),
    { contactTypeCode: '700', contactValue: tempParty.email },
  ];
  party.addresses = [
    {
      addressTypeCode: 'MAILING',
      conscriptionNumber: tempParty.conscriptionNumber,
      countryCode: 'CZ',
      municipality: tempParty.municipality,
      street: tempParty.street,
      zipCode: tempParty.zipCode,
    },
  ];

  agreement.roles.push({
    party: {
      partyIdentifiers: [
        {
          partyIdentifierTypeCode: 'ORGCODE',
          partyIdentifierValue: referralCode,
        },
        {
          partyIdentifierTypeCode: 'BROKERID',
          partyIdentifierValue: '1',
        },
      ],
      partyTypeCode: '1',
    },
    roleCode: 'DISTRIBUTOR',
  });

  return trimStringsInObj(agreement);
}

/**
 * Trims every string in the object
 */
function trimStringsInObj<T>(obj: T): T {
  if (typeof obj === 'string') {
    return obj.trim() as T;
  } else if (Array.isArray(obj)) {
    return obj.map(item => trimStringsInObj(item)) as T;
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, trimStringsInObj(value)]),
    ) as T;
  }

  return obj;
}

export { calculationToModel, modelToAgreement, modelToCalculation };
